import { isSubPortfolioLifeRef } from '@festina/life-general-types/life'
import type { SubPortfolioLifeRef } from '@festina/unitlink-general-types/unitlink'
import { typeUtils, type ValidatedKey } from '@festina-life/core'
import { FormikSelect } from '@festina-life/ui/formikForms'
import { useTranslate } from '@festina-life/ui/hooks'
import { useField } from 'formik'
import { useMemo } from 'react'

import { useTranslateCover } from '../../../hooks/useTranslateCover'
import { useHook } from '../../../lenteHooks'

type Props = {
  fieldNamePrefix: ValidatedKey
}

export const SubPortfolioLifeRefFields = ({ fieldNamePrefix }: Props) => {
  const translate = useTranslate()
  const translateCover = useTranslateCover()
  const renderPolicy = useHook('pages.commitment.useLazyRenderPolicy')()

  const { tenantId, ownerId } = useHook('routing.general.useEntityParams')()
  const { data: portfolio } = useHook(
    'fetchers.unitlink.useFetchPortfolioVersionLatest',
  )({
    tenantId,
    ownerId,
  })

  const policyKeyToCoverKeysMap = useMemo(() => {
    const result: Record<string, string[]> = {}

    for (const groupVersion of portfolio?.subPortfolioGroupVersions ?? []) {
      for (const subPortfolio of groupVersion?.subPortfolioVersions ?? []) {
        if (isSubPortfolioLifeRef(subPortfolio?.subPortfolioRef)) {
          const { policyKey, coverKey } = subPortfolio.subPortfolioRef

          if (policyKey && coverKey) {
            if (!result[policyKey]) {
              result[policyKey] = []
            }
            result[policyKey].push(coverKey)
          }
        }
      }
    }

    return result
  }, [portfolio])

  const policyKeys = Object.keys(policyKeyToCoverKeysMap)

  const validateKey =
    typeUtils.createNestedValidator<SubPortfolioLifeRef>(fieldNamePrefix)

  const [{ value: policyKey }] = useField(validateKey('policyKey'))

  return (
    <>
      <FormikSelect
        name={validateKey('policyKey')}
        label={translate('subPortfolioCorrection.policyKey')}
        options={policyKeys}
        renderFn={renderPolicy}
      />
      <FormikSelect
        name={validateKey('coverKey')}
        label={translate('subPortfolioCorrection.coverKey')}
        options={policyKeyToCoverKeysMap[policyKey] ?? []}
        renderFn={(coverKey) => translateCover(coverKey)}
      />
    </>
  )
}
